import { useMemo, useState } from 'react';

import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { SeatBasedUsageResponse } from '@/api/types/backend-dto-types';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

function formatPeriodLabel(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const fmt = (d: Date) => d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  return `${fmt(s)} – ${fmt(e)}`;
}

export function SeatPeriodChart({ data }: { data: SeatBasedUsageResponse }) {
  const periodOptions = useMemo(
    () =>
      data.periods.map((p, idx) => ({
        idx,
        label: formatPeriodLabel(p.periodStart, p.periodEnd),
      })),
    [data.periods],
  );

  // Default to the last (current) period
  const [selectedIdx, setSelectedIdx] = useState<number>(data.periods.length - 1);
  const period = data.periods[selectedIdx];

  const todayKey = new Date().toISOString().split('T')[0];
  const isCurrentPeriod = selectedIdx === data.periods.length - 1;

  const chartData = useMemo(() => {
    if (!period) return [];
    // For historical periods, show all data as solid
    if (!isCurrentPeriod) {
      return period.dailyCumulative.map((d) => ({
        date: d.date,
        credits: d.cumCredits,
      }));
    }

    // For current period, split into actual (solid) and remaining (dotted)
    const lastActualCredits = period.dailyCumulative.filter((d) => d.date <= todayKey).slice(-1)[0]?.cumCredits ?? 0;

    return period.dailyCumulative.map((d) => {
      const isPast = d.date <= todayKey;
      const isToday = d.date === todayKey;
      return {
        date: d.date,
        credits: isPast ? d.cumCredits : undefined,
        // Start projected on today so the lines connect, then carry forward
        projected: isToday || !isPast ? lastActualCredits : undefined,
      };
    });
  }, [period, isCurrentPeriod, todayKey]);

  if (data.periods.length === 0) return null;

  return (
    <div className="-mx-2 mt-3 rounded-xl border bg-gray-50 p-5 py-3">
      <Accordion type="single" collapsible>
        <AccordionItem value="period-chart">
          <AccordionTrigger className="py-0">
            <span className="font-medium text-gray-800">Credit Usage by Period</span>
          </AccordionTrigger>
          <AccordionContent className="pt-2">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-gray-500">Cumulative credit usage compared against your credit limit.</p>
              <div className="flex flex-col items-end gap-2">
                <Select value={String(selectedIdx)} onValueChange={(v) => setSelectedIdx(parseInt(v, 10))}>
                  <SelectTrigger className="w-48 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {periodOptions.map((opt) => (
                      <SelectItem key={opt.idx} value={String(opt.idx)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="creditsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-teal-500)" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="var(--color-teal-500)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ transform: 'translate(0, 6)' }}
                  className="fill-gray-500 text-xs"
                  tickLine={false}
                  axisLine={false}
                  minTickGap={40}
                />
                <YAxis
                  className="fill-gray-500 text-xs"
                  tick={{ transform: 'translate(-4, 0)' }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, data.creditLimit * 1.1]}
                  ticks={[
                    Math.round(data.creditLimit * 0.25),
                    Math.round(data.creditLimit * 0.5),
                    Math.round(data.creditLimit * 0.75),
                    data.creditLimit,
                  ]}
                  tickFormatter={(v: number) =>
                    new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v)
                  }
                />
                <Tooltip
                  content={({ payload, label }) => {
                    if (!payload || payload.length === 0) return null;
                    // Show the actual credits value (prefer 'credits' over 'projected')
                    const creditsEntry =
                      payload.find((e) => e.dataKey === 'credits' && e.value != null) ??
                      payload.find((e) => e.dataKey === 'projected' && e.value != null);
                    return (
                      <div className="rounded-lg border bg-white p-3 text-sm shadow-sm">
                        <p className="mb-2 border-b border-gray-200 pb-2 font-medium text-gray-700">{label}</p>
                        {creditsEntry != null && (
                          <div className="flex items-center justify-between gap-6">
                            <div className="flex items-center gap-1.5">
                              <span className="h-[3px] w-3.5 shrink-0 rounded-full bg-teal-500" />
                              <span className="text-gray-600">Cumulative credits</span>
                            </div>
                            <span className="font-medium text-gray-800">
                              {new Intl.NumberFormat('en-US').format(creditsEntry.value as number)}
                            </span>
                          </div>
                        )}
                        <div className="mt-1 flex items-center justify-between gap-6">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="h-[3px] w-3.5 shrink-0 rounded-full bg-red-400"
                              style={{ borderStyle: 'dashed' }}
                            />
                            <span className="text-gray-400">Limit</span>
                          </div>
                          <span className="text-gray-400">
                            {new Intl.NumberFormat('en-US').format(data.creditLimit)}
                          </span>
                        </div>
                      </div>
                    );
                  }}
                />
                <ReferenceLine
                  y={data.creditLimit}
                  stroke="var(--color-red-400)"
                  strokeDasharray="6 4"
                  label={{
                    value: `Limit: ${new Intl.NumberFormat('en-US', { notation: 'compact' }).format(data.creditLimit)}`,
                    position: 'right',
                    fontSize: 11,
                    fill: 'var(--color-red-500)',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="credits"
                  stroke="var(--color-teal-500)"
                  strokeWidth={2}
                  fill="url(#creditsFill)"
                  dot={false}
                  name="Credits used"
                  connectNulls={false}
                />
                {isCurrentPeriod && (
                  <Area
                    type="monotone"
                    dataKey="projected"
                    stroke="var(--color-teal-500)"
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    fill="url(#creditsFill)"
                    dot={false}
                    name="Remaining period"
                    connectNulls={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
